import React from 'react';
import { FileCheck, Users, AlertTriangle, Scale } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-paper">
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-6 py-24">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-panel rounded-none mb-6">
            <FileCheck className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-ink mb-4">
            Terms & Conditions
          </h1>
          <p className="text-muted text-lg">
            Last updated: September 27, 2025
          </p>
        </div>

        {/* Content */}
        <div className="space-y-12">
          <section>
            <p className="text-muted leading-relaxed mb-4">
              Welcome to DressUp AI ("DressUp," "we," "our," or "us"). These Terms & Conditions ("Terms") govern your access to and use of the DressUp mobile application and related services (collectively, the "Service"). By downloading, accessing, or using the Service, you agree to be bound by these Terms.
            </p>
            <div className="bg-surface border-l-4 border-accent p-6 rounded-none">
              <p className="text-muted font-medium">
                If you do not agree, please do not use the Service.
              </p>
            </div>
          </section>

          <div className="border-t border-line" />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-surface rounded-none flex items-center justify-center">
                <Users className="h-6 w-6 text-ink" />
              </div>
              <h2 className="text-2xl font-bold text-ink">
                1. Eligibility
              </h2>
            </div>
            <p className="text-muted leading-relaxed mb-4">
              You must be at least 13 years old to use the Service.
            </p>
            <p className="text-muted leading-relaxed">
              By using DressUp, you confirm that you have the legal capacity to enter into this agreement.
            </p>
          </section>

          <div className="border-t border-line" />

          <section>
            <h2 className="text-2xl font-bold text-ink mb-4">
              2. Your Account
            </h2>
            <ul className="space-y-3 text-muted">
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                You are responsible for maintaining the confidentiality of your account and login details.
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                You are responsible for all activities under your account.
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                You agree to provide accurate, current, and complete information when creating your account.
              </li>
            </ul>
          </section>

          <div className="border-t border-line" />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-surface rounded-none flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-ink" />
              </div>
              <h2 className="text-2xl font-bold text-ink">
                3. Use of the Service
              </h2>
            </div>
            <p className="text-muted leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="space-y-3 text-muted mb-4">
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                Misuse the Service for unlawful or harmful purposes.
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                Attempt to hack, reverse-engineer, or disrupt our systems.
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                Upload or share inappropriate, offensive, or illegal content.
              </li>
              <li className="flex gap-3">
                <span className="text-ink font-bold">•</span>
                Violate the rights of others, including intellectual property rights.
              </li>
            </ul>
            <p className="text-muted leading-relaxed">
              We reserve the right to suspend or terminate accounts that violate these rules.
            </p>
          </section>

          <div className="border-t border-line" />

          <section>
            <h2 className="text-2xl font-bold text-ink mb-4">
              4. Intellectual Property
            </h2>
            <p className="text-muted leading-relaxed mb-4">
              All content, features, and functionality of the Service (including design, text, graphics, and software) are the property of DressUp and are protected by copyright and trademark laws.
            </p>
            <p className="text-muted leading-relaxed mb-4">
              You may not copy, modify, distribute, or resell any part of the Service without our permission.
            </p>
            <p className="text-muted leading-relaxed">
              Product catalogs and brand assets shown in the app remain the property of their respective owners.
            </p>
          </section>

          <div className="border-t border-line" />

          <section>
            <h2 className="text-2xl font-bold text-ink mb-4">
              5. Fashion Recommendations Disclaimer
            </h2>
            <p className="text-muted leading-relaxed">
              DressUp provides personalized style recommendations using AI. These recommendations are suggestions only and may not always be accurate or suitable. We are not responsible for outcomes related to following or purchasing based on these recommendations.
            </p>
          </section>

          <div className="border-t border-line" />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-surface rounded-none flex items-center justify-center">
                <Scale className="h-6 w-6 text-ink" />
              </div>
              <h2 className="text-2xl font-bold text-ink">
                6. Governing Law
              </h2>
            </div>
            <p className="text-muted leading-relaxed">
              These Terms are governed by and construed under the laws of Pakistan.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
